import React, { useState } from 'react';
import clsx from 'clsx';
import { Info } from 'lucide-react';

import { Trans, useLingui } from '@lingui/react/macro';

import { Badge } from '../ui/badge';

interface DurationWithHoverDetailsProps {
  duration: number;
  className?: string;
  compact?: boolean;
}

export function DurationWithHoverDetails({
  duration,
  className = "",
  compact = false
}: DurationWithHoverDetailsProps) {
  const { t } = useLingui();
  const [isHovered, setIsHovered] = useState(false);
  
  if (compact) {
    return (
      <div className="relative flex-1">
        <div 
          className={clsx(
            "flex items-center justify-between cursor-pointer transition-all duration-200 hover:bg-brand-dark-blue/5 rounded px-2 py-1", 
            className
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <span className="text-xs font-medium text-brand-dark-blue/80"><Trans>Duration:</Trans></span>
          <div className="flex items-center gap-1">
            <Badge className="bg-brand-dark-blue text-white text-xs font-semibold">
              {Math.round(duration)}s
            </Badge>
            <Info className={clsx("h-3 w-3 text-brand-dark-blue transition-transform duration-200", isHovered && "scale-110")} />
          </div>
        </div>

        {/* Compact hover tooltip */}
        {isHovered && (
          <div className="absolute z-20 top-full left-0 right-0 mt-2 p-4 rounded-lg border-2 border-brand-dark-blue/30 bg-white shadow-xl transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0">
            <h4 className="text-sm font-bold mb-3 flex items-center gap-2 text-brand-dark-blue">
              <Info className="h-4 w-4" />
              <Trans>Duration Analysis</Trans>
            </h4>

            <div className="text-xs p-3 rounded mb-3 bg-brand-dark-blue/5">
              <div className="flex items-start gap-2">
                <Info className="h-3 w-3 mt-0.5 shrink-0 text-brand-dark-blue" />
                <span className="font-medium leading-relaxed text-brand-dark-blue">
                  {duration < 30
                    ? t`Short recording - consider longer sessions for more detailed feedback`
                    : duration < 120
                    ? t`Good duration for practice - allows for comprehensive analysis`
                    : duration < 300
                    ? t`Extended session - excellent for in-depth analysis and improvement tracking`
                    : t`Very long session - great for detailed performance insights`}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <h5 className="text-xs font-semibold text-brand-dark-blue">
                <Trans>💡 Duration Tips:</Trans>
              </h5>
              <ul className="space-y-1">
                {duration < 30 ? (
                  <>
                    <li className="text-xs text-gray-600 flex items-start gap-2">
                      <span className="text-brand-gold font-bold">•</span>
                      <span><Trans>Try recording for 45-60 seconds for better analysis</Trans></span>
                    </li>
                    <li className="text-xs text-gray-600 flex items-start gap-2">
                      <span className="text-brand-gold font-bold">•</span>
                      <span><Trans>Longer recordings provide more speech patterns to analyze</Trans></span>
                    </li>
                  </>
                ) : duration < 120 ? (
                  <>
                    <li className="text-xs text-gray-600 flex items-start gap-2">
                      <span className="text-brand-gold font-bold">•</span>
                      <span><Trans>Perfect length for focused practice sessions</Trans></span>
                    </li>
                    <li className="text-xs text-gray-600 flex items-start gap-2">
                      <span className="text-brand-gold font-bold">•</span>
                      <span><Trans>Allows for natural speech flow and comprehensive feedback</Trans></span>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="text-xs text-gray-600 flex items-start gap-2">
                      <span className="text-brand-gold font-bold">•</span>
                      <span><Trans>Extended sessions are great for tracking improvement over time</Trans></span>
                    </li>
                    <li className="text-xs text-gray-600 flex items-start gap-2">
                      <span className="text-brand-gold font-bold">•</span>
                      <span><Trans>Provides rich data for detailed performance analysis</Trans></span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div 
        className={clsx(
          "p-4 rounded-lg border-2 border-brand-dark-blue/30 bg-gradient-to-br from-brand-dark-blue/5 to-brand-gold/5 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02]", 
          className
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-bold text-brand-dark-blue"><Trans>Duration</Trans></h3>
          <Info className={clsx("h-4 w-4 text-brand-dark-blue transition-transform duration-200", isHovered && "scale-110")} />
        </div>

        <Badge className="bg-brand-dark-blue text-white font-semibold">
          {Math.round(duration)}s
        </Badge>
      </div>

      {/* Hover tooltip */}
      {isHovered && (
        <div className="absolute z-10 top-full left-0 right-0 mt-2 p-4 rounded-lg border-2 border-brand-dark-blue/30 bg-white shadow-xl transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0">
          <h4 className="text-sm font-bold mb-3 flex items-center gap-2 text-brand-dark-blue">
            <Info className="h-4 w-4" />
            <Trans>Duration Analysis</Trans>
          </h4>

          <div className="text-xs p-3 rounded mb-3 bg-brand-dark-blue/5">
            <div className="flex items-start gap-2">
              <Info className="h-3 w-3 mt-0.5 shrink-0 text-brand-dark-blue" />
              <span className="font-medium leading-relaxed text-brand-dark-blue">
                {duration < 30
                  ? t`Short recording - consider longer sessions for more detailed feedback`
                  : duration < 120
                  ? t`Good duration for practice - allows for comprehensive analysis`
                  : duration < 300
                  ? t`Extended session - excellent for in-depth analysis and improvement tracking`
                  : t`Very long session - great for detailed performance insights`}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <h5 className="text-xs font-semibold text-brand-dark-blue">
              <Trans>💡 Duration Tips:</Trans>
            </h5>
            <ul className="space-y-1">
              {duration < 30 ? (
                <>
                  <li className="text-xs text-gray-600 flex items-start gap-2">
                    <span className="text-brand-gold font-bold">•</span>
                    <span><Trans>Try recording for 45-60 seconds for better analysis</Trans></span>
                  </li>
                  <li className="text-xs text-gray-600 flex items-start gap-2">
                    <span className="text-brand-gold font-bold">•</span>
                    <span><Trans>Longer recordings provide more speech patterns to analyze</Trans></span>
                  </li>
                </>
              ) : duration < 120 ? (
                <>
                  <li className="text-xs text-gray-600 flex items-start gap-2">
                    <span className="text-brand-gold font-bold">•</span>
                    <span><Trans>Perfect length for focused practice sessions</Trans></span>
                  </li>
                  <li className="text-xs text-gray-600 flex items-start gap-2">
                    <span className="text-brand-gold font-bold">•</span>
                    <span><Trans>Allows for natural speech flow and comprehensive feedback</Trans></span>
                  </li>
                </>
              ) : (
                <>
                  <li className="text-xs text-gray-600 flex items-start gap-2">
                    <span className="text-brand-gold font-bold">•</span>
                    <span><Trans>Extended sessions are great for tracking improvement over time</Trans></span>
                  </li>
                  <li className="text-xs text-gray-600 flex items-start gap-2">
                    <span className="text-brand-gold font-bold">•</span>
                    <span><Trans>Provides rich data for detailed performance analysis</Trans></span>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
